import React from 'react';
import useUser from './api/useUser';
import { useIsE2E } from './useIsE2E';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { flushAnalyticsEvents } from '../utils/analytics/queue';
import { recordSessionStart, recordSessionStop } from '../utils/analytics/record';

/**
 * A browser session runs for as long as the tab stays visible: becoming visible starts one, becoming hidden
 * ends it. That is the behaviour Pinpoint's automatic session tracking had, so session counts stay
 * comparable across the migration.
 */
const trackTabVisibility = () => {
    const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
            recordSessionStop();
            // Hiding is the last chance to send before a possible close. The request uses the normal
            // client, so the browser may cancel it if the page is already tearing down; on a plain
            // tab switch it completes.
            void flushAnalyticsEvents();
        } else {
            recordSessionStart();
        }
    };

    // Seed from the current state rather than assuming visible: mounting in a background tab should
    // not open a session. Pinpoint's listener did the same on registration.
    handleVisibilityChange();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
};

/**
 * An app session runs from coming to the foreground until going to the background. iOS also reports
 * `inactive` for interruptions that never leave the app — Control Center, the app switcher, a system
 * alert — and those keep the current session rather than splitting it in two.
 */
const trackAppState = () => {
    let needsSession = true;

    const handleAppStateChange = (state: AppStateStatus) => {
        if (state === 'background') {
            recordSessionStop();
            // The OS can suspend or kill a backgrounded app without further notice, so this is the last
            // dependable chance to send what is queued.
            void flushAnalyticsEvents();
            needsSession = true;
        } else if (state === 'active' && needsSession) {
            recordSessionStart();
            needsSession = false;
        }
    };

    // Seed from the current state: an app mounted while still in the background waits until it is
    // brought forward before opening a session.
    handleAppStateChange(AppState.currentState);
    const subscription = AppState.addEventListener('change', handleAppStateChange);

    return () => subscription.remove();
};

/** Track analytics sessions for the signed-in user. */
export const useConfigureAnalytics = () => {
    const { user } = useUser();
    const isE2E = useIsE2E();
    const userId = user === 'loading' ? undefined : user.userId;

    React.useEffect(() => {
        if (!userId || isE2E) return;

        const stopTracking = Platform.OS === 'web' ? trackTabVisibility() : trackAppState();

        return () => {
            stopTracking();
            // Close the session and send what is queued before letting go of the user it belongs to.
            // Sign-out already does both while the user's credentials are still valid (see
            // useSignOutAndResetApp), because by the time this cleanup runs they have been cleared; on
            // that path these calls find nothing left to do.
            recordSessionStop();
            void flushAnalyticsEvents();
        };
    }, [userId, isE2E]);
};
